import { isEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import type { IntlKey } from 'locales';
import { uniqBy } from 'lodash-es';
import type { Item } from 'models/IItem';
import { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { camelCaseToWords } from 'utils/string';

interface OwnProps {
  id: string;
  className?: string;
  value: string;
  e2e?: string;
  translationKey: IntlKey;
  availableItems: ReadonlyArray<Item<string>>;
  isInvalid?: boolean;

  disabled?: boolean;
  onValueChange: (newValue: string) => void;
}

export const ListField: FC<OwnProps> = ({
  id,
  className,
  value,
  e2e,
  availableItems,
  translationKey,
  isInvalid,
  disabled,
  onValueChange,
}) => {
  const { formatMessage } = useIntl();
  const onChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value;
      if (newValue !== value) {
        onValueChange(newValue);
      }
    },
    [value, onValueChange],
  );

  const availableOptions = useMemo(() => {
    const sanitized = uniqBy(availableItems, 'value');
    if (sanitized.length < availableItems.length) {
      throw new Error(
        `ListField<${id}> contains some bad values (duplicate) : ${JSON.stringify(availableItems.map(x => x.value))}`,
      );
    }

    return sanitized.map(({ label, value: val }) => {
      if (isEmpty(label)) {
        const lab = val ?? '';
        return { value: val, label: lab };
      }
      return {
        value: val,
        label: formatMessage({ id: `field.${label}` as any, defaultMessage: camelCaseToWords(label) }),
      };
    });
  }, [availableItems, formatMessage, id]);

  const isReadOnly = availableOptions.length === 1;

  return (
    <BlockOrientationFormGroup inputId={id} label={translationKey}>
      <select
        id={id}
        className={classNames('form-select', { 'is-invalid': isInvalid }, className)}
        value={value || ''}
        onChange={onChange}
        disabled={isReadOnly || disabled}
        data-e2e={e2e || `${id}-select`}
      >
        {availableOptions.map(({ value: itemValue, label: itemLabel }) => (
          <option key={itemValue || 'null'} value={itemValue}>
            {itemValue === ''
              ? ''
              : itemLabel === ''
                ? ''
                : formatMessage({ id: `form.${id}${itemValue}` as any, defaultMessage: itemLabel })}
          </option>
        ))}
      </select>
    </BlockOrientationFormGroup>
  );
};

ListField.displayName = 'ListField';
